import { THEMEABLE_TOKENS, type Theme, type ThemeToken } from './tokens';

/* Values come from partner configuration into a <style> tag, so anything that
   could close the rule or fetch a remote resource is rejected. */
const FORBIDDEN = /[;{}<>\\]|\/\*|\*\/|url\(|expression\(|@import/i;

const SHAPE_BY_KIND = {
  // #abc, #aabbcc, #aabbccdd, rgb()/hsl()/oklch()/color-mix(), var(), or a bare keyword
  color: /^(#[0-9a-f]{3,8}|[a-z-]+\([^)]*\)|var\(--[a-z0-9-]+(,[^()]*)?\)|[a-z]+)$/i,
  // 1.25rem, 0, calc(...), var()
  length: /^(-?[\d.]+[a-z%]*|calc\([^()]*\)|var\(--[a-z0-9-]+(,[^()]*)?\))$/i,
  // Quoted or bare family names, commas, and var()
  font: /^[\w\s,'"-]+$|^var\(--[a-z0-9-]+(,[^()]*)?\)$/i,
} as const;

export const isValidThemeValue = (token: ThemeToken, value: string): boolean => {
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > 200) return false;
  if (FORBIDDEN.test(trimmed)) return false;
  return SHAPE_BY_KIND[THEMEABLE_TOKENS[token].kind].test(trimmed);
};

/**
 * Turn a theme into a CSS rule. An invalid value is dropped rather than thrown,
 * so one malformed partner color costs that color and not the page.
 */
export const serializeTheme = (theme: Theme, selector = ':root'): string => {
  const declarations = (Object.keys(theme) as Array<ThemeToken>)
    .flatMap(token => {
      const value = theme[token];
      if (value === undefined) return [];
      if (!isValidThemeValue(token, value)) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`[design-system] dropped unsafe theme value for "${token}": ${value}`);
        }
        return [];
      }
      return [`${THEMEABLE_TOKENS[token].cssVar}:${value.trim()}`];
    })
    .join(';');

  return declarations.length > 0 ? `${selector}{${declarations}}` : '';
};
